import React, { useState, useEffect, useCallback } from 'react';
import CompleteClass from './CompleteClass';
import Calendar from './Calendar';
import VideoSession from './VideoSession';
import { api } from '../api';
import toast from 'react-hot-toast';

export default function StudentView({ currentUser, studentId }) {
  const targetId = studentId || null; // null = "me" to the API
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeClass, setActiveClass] = useState(null);
  const [videos, setVideos] = useState([]);
  const [activeVideoId, setActiveVideoId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setChecklist(await api.getToday(targetId));
    } catch {
      toast.error('Could not load today\'s classes');
    } finally {
      setLoading(false);
    }
  }, [targetId]);

  useEffect(() => { load(); }, [load]);

  // Video assignments. A failure here must never get in the way of the class list, so it is quiet.
  const loadVideos = useCallback(async () => {
    try { setVideos(await api.getVideoToday(targetId)); } catch { setVideos([]); }
  }, [targetId]);
  useEffect(() => { loadVideos(); }, [loadVideos]);

  const activeVideo = videos.find(v => v.id === activeVideoId);
  if (activeVideo && !studentId) {
    return (
      <div className="student-view">
        <VideoSession item={activeVideo} onExit={() => { setActiveVideoId(null); loadVideos(); }} onChanged={loadVideos} />
      </div>
    );
  }

  const doneCount = checklist.filter(c => c.done).length;
  const allDone = checklist.length > 0 && doneCount === checklist.length;

  return (
    <div className="student-view">
      <div className="today-card">
        <h2>Today's Classes</h2>
        {loading ? (
          <div className="loading">Loading…</div>
        ) : checklist.length === 0 ? (
          <p className="empty-state">No classes scheduled today. 🎉</p>
        ) : (
          <>
            <p className="progress-label">{doneCount} / {checklist.length} done{allDone ? ' — nice work!' : ''}</p>
            <ul className="class-checklist">
              {checklist.map(c => (
                <li key={c.class_id} className={`class-checklist-item ${c.done ? 'done' : ''}`}>
                  <div className="class-checklist-info">
                    <span className="class-checklist-name">{c.name}</span>
                    {c.instructions && <span className="class-checklist-instructions">{c.instructions}</span>}
                    {c.done && (
                      <span className="class-checklist-done-at">
                        Done at {new Date(c.completed_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                        {c.notes ? ` — "${c.notes}"` : ''}
                      </span>
                    )}
                  </div>
                  {!c.done && (
                    <button className="btn-primary btn-small" onClick={() => setActiveClass(c)}>Mark done</button>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {videos.length > 0 && (
        <div className="today-card" style={{ marginTop: 16 }}>
          <h2>Today's Videos</h2>
          <ul className="class-checklist">
            {videos.map(v => {
              const doneParts = v.parts.filter(p => p.complete).length;
              const label = v.part_from === v.part_to ? `Part ${v.part_from}` : `Parts ${v.part_from}–${v.part_to}`;
              return (
                <li key={v.id} className={`class-checklist-item ${v.completed_at ? 'done' : ''}`}>
                  <div className="class-checklist-info">
                    <span className="class-checklist-name">{v.video.title || 'Video'}</span>
                    <span className="class-checklist-instructions">
                      {label} · {doneParts} of {v.parts.length} finished{v.overdue ? ' · from an earlier day' : ''}
                    </span>
                    {v.completed_at && (
                      <span className="class-checklist-done-at">
                        Done at {new Date(v.completed_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  {!v.completed_at && !studentId && (
                    <button className="btn-primary btn-small" onClick={() => setActiveVideoId(v.id)}>
                      {doneParts > 0 || v.parts.some(p => p.attempts > 0) ? 'Continue' : 'Start'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {!studentId && (
        <div className="export-row">
          <Calendar userId={null} />
          <button className="btn-secondary" onClick={() => api.exportCSV(null, currentUser.name).catch(() => toast.error('Export failed'))}>
            ⬇ Export My History (CSV)
          </button>
        </div>
      )}

      {activeClass && (
        <CompleteClass
          classItem={activeClass}
          onCancel={() => setActiveClass(null)}
          onDone={() => { setActiveClass(null); load(); }}
        />
      )}
    </div>
  );
}
